
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import store from "@/store";
import { useStore } from "vuex";
import AppIcon from "@/components/common/AppIcon.vue";
import AppButton from "@/components/common/AppButton.vue";
import CardKpi from "@/components/card/CardKpi.vue";
import AppSection from "@/components/common/AppSection.vue";
import CardGroup from "@/components/card/CardGroup.vue";
import CardListItem from "@/components/card/CardListItem.vue";
import MailModel from "@/models/MailModel";
import { StatsEmailModel } from "@/models/StatsModel";
import { useRouter } from "vue-router";
import MailPreviewContent from "@/components/mails/MailPreviewContent.vue";
import SegmentModel from "@/models/SegmentModel";
import BounceMetrics from "@/components/mails/BounceMetrics.vue";
import useEmailBounceAnalytics from "@/composables/useEmailBounceAnalytics";

export default defineComponent({
    name: "MailViewPage",
    components: {
        CardKpi,
        AppSection,
        CardGroup,
        AppIcon,
        AppButton,
        CardListItem,
        MailPreviewContent,
        BounceMetrics
    },
    async beforeRouteEnter(to, from, next) {
        if (store.state.mails.active) {
            next();
            return;
        }

        if (to.params.id === "0") {
            next({ name: "mails-list" });
            return;
        }

        const mailInList = store.getters["mails/getById"](
            parseInt(to.params.id as string)
        );

        if (mailInList !== undefined) {
            store.commit("mails/SET_ACTIVE", mailInList);
        } else {
            store.commit("app/SET_LOADING", true);
            await store.dispatch("mails/fetchActive", to.params.id);
            store.commit("app/SET_LOADING", false);
        }
        next();
    },
    setup() {
        const store = useStore();
        const router = useRouter();
        const { mails, segments } = store.state;
        const mail = computed((): MailModel => store.state.mails.active);
        const mailStats = computed(
            (): StatsEmailModel =>
                store.getters["stats/getStatsByEmailId"](
                    store.state.mails.active.id
                )
        );
        const clickThroughRate = computed((): number => {
            if (mailStats.value && mail.value.trackables) {
                const totalClicks = mail.value.trackables
                    .map(t => parseInt((t.hits as unknown) as string, 10))
                    .reduce((sum, hits) => sum + hits, 0);
                return mailStats.value.total_sent > 0
                    ? (totalClicks / mailStats.value.total_sent) * 100
                    : 0;
            }
            return 0;
        });
        const clickToOpenRate = computed((): number => {
            if (mailStats.value && mail.value.trackables) {
                const totalClicks = mail.value.trackables
                    .map(t => parseInt((t.hits as unknown) as string, 10))
                    .reduce((sum, hits) => sum + hits, 0);
                return mailStats.value.total_open > 0
                    ? (totalClicks / mailStats.value.total_open) * 100
                    : 0;
            }
            return 0;
        });

        const mailSegment = computed(() => {
                return segments.list.find(
                    (segment: SegmentModel) =>
                        segment.id ===
                        (mails.active.lists.length
                            ? mails.active.lists[0].id
                            : 0)
                );
            }),
            listLabel = computed((): string => {
                return mailSegment.value
                    ? mailSegment.value.name
                    : mails.active.lists.length
                    ? mails.active.lists[0].name
                    : "-";
            }),
            listLabelCount = computed((): number | string => {
                return mailSegment.value ? mailSegment.value.leadCount : "-";
            });
        const goToMails = () => {
            router.push({ name: "mails-list" });
        };
        const duplicateMail = () => {
            store.commit("mails/SET_DUPLICATE");
            router.push({ name: "mail-edit", params: { id: 0 } });
        };
        const currency = computed(() => store.state.store.currency);
        const showBounceMetrics = ref(false);
        
        // Sorting state for trackables
        const sortBy = ref('hits');
        const sortDirection = ref('desc');

        // Sorted trackables computed property
        const sortedTrackables = computed(() => {
            if (!mail.value.trackables) return [];
            
            const trackables = [...mail.value.trackables];
            
            return trackables.sort((a, b) => {
                let aValue, bValue;
                
                switch (sortBy.value) {
                    case 'url':
                        aValue = a.url.toLowerCase();
                        bValue = b.url.toLowerCase();
                        break;
                    case 'unique_hits':
                        aValue = parseInt(a.unique_hits as unknown as string, 10);
                        bValue = parseInt(b.unique_hits as unknown as string, 10);
                        break;
                    case 'hits':
                    default:
                        aValue = parseInt(a.hits as string, 10);
                        bValue = parseInt(b.hits as string, 10);
                        break;
                }
                
                if (sortDirection.value === 'asc') {
                    return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                } else {
                    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
                }
            });
        });

        // Sort function
        const handleSort = (column: string) => {
            if (sortBy.value === column) {
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            } else {
                sortBy.value = column;
                sortDirection.value = 'desc';
            }
        };

        const truncateUrl = (url: string): string => {
            const maxLength = 50;
            if (url.length <= maxLength) {
                return url;
            }
            return url.substring(0, maxLength) + '...';
        };

        onMounted(() => {
            window.analytics.track("Mail Viewed", {
                userId: store.getters["user/emailHash"],
                mailId: store.state.mails.active.id,
                mailStatus: store.getters["mails/activeStatus"]
            });
        });

        // Bounce Analytics
        const {
            bounceData,
            loading: bounceLoading,
            fetchEmailBounceAnalytics
        } = useEmailBounceAnalytics();

        // Fetch bounce analytics when mail ID is available
        watch(
            () => mail.value?.id,
            emailId => {
                if (emailId) {
                    fetchEmailBounceAnalytics(emailId);
                }
            },
            { immediate: true }
        );

        return {
            mail,
            mailStats,
            clickThroughRate,
            clickToOpenRate,
            listLabel,
            listLabelCount,
            goToMails,
            duplicateMail,
            currency,
            bounceData,
            bounceLoading,
            showBounceMetrics,
            truncateUrl,
            sortedTrackables,
            handleSort,
            sortBy,
            sortDirection
        };
    }
});
